"use client";

import React, { useState, useEffect } from "react";
import { Check, LoaderPinwheel } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
export default function JoinTheNest() {
  return <section data-section-id="27435" id="join-the-movement" className="dark bg-gradient-to-b from-[#330000] to-background text-foreground py-16 px-4 md:py-24 relative overflow-hidden">
      <div className="container mx-auto max-w-3xl relative z-10">
        <div className="bg-card/40 backdrop-blur-sm text-card-foreground rounded-none p-8 md:p-12 shadow-2xl border border-white/10 relative">
          {}
          <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-primary rounded-none" />
          <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-primary rounded-none" />
          <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-primary rounded-none" />
          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-primary rounded-none" />

          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-sans font-black mb-4 tracking-tighter uppercase text-white">
              NEVER LEAVE A BROTHER BEHIND
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto font-sans tracking-tight">
              Support the mission. Wear the message. Help build a community that refuses to leave anyone behind.
            </p>
          </div>

          <FormProvider formId="	join_movement_form_home" sectionName="join-the-movement" defaultValues={{
          name: "",
          email: "",
          interests: []
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          },
          interests: {
            required: false
          }
        }} submitText="Enlist" successMessage="Welcome to the ranks. We have your six. Check your email for further orders." errorMessage="The line is down. Please try to re-establish connection.">
            <JoinNestForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function JoinNestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgress(10);
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 300);
    } else if (isSubmitted && !submitError) {
      setProgress(100);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting, isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div className="bg-background/80 backdrop-blur-md rounded-none p-8 text-center border border-primary/30 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 text-primary rounded-none flex items-center justify-center mx-auto mb-4 border border-primary/50">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-sans font-black text-white mb-2 uppercase tracking-tighter">
          Rally Point Secured
        </h3>
        <p className="text-muted-foreground mb-6 font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-white transition-all uppercase font-bold">
          Enlist another member
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-bold uppercase text-xs tracking-widest">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="IDENTIFY YOURSELF" {...field} className="bg-white text-black border-none rounded-none focus-visible:ring-primary h-12 placeholder:text-zinc-400 font-sans font-medium" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60 text-[10px] uppercase tracking-wider">
                Optional identification.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-bold uppercase text-xs tracking-widest">
                Comms Channel <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@ADDRESS.COM" {...field} className="bg-white text-black border-none rounded-none focus-visible:ring-primary h-12 placeholder:text-zinc-400 font-sans font-medium" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60 text-[10px] uppercase tracking-wider">
                Required for mission updates.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white font-bold uppercase text-xs tracking-widest block mb-3">
              Mission Objectives
            </FormLabel>
            <div className="grid grid-cols-2 gap-3">
              {["Apparel Drops", "Rally Points", "Support Resources", "Mission Stories"].map((item, i) => <label key={item} data-index={i} className="flex items-center space-x-3 cursor-pointer group">
                    <div className="relative flex items-center justify-center">
                      <input type="checkbox" className="peer sr-only" checked={(field.value || []).includes(item)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              if (checked) {
                field.onChange([...current, item]);
              } else {
                field.onChange(current.filter((val: string) => val !== item));
              }
            }} />
                      <div className="w-5 h-5 border-2 border-white/20 rounded-none peer-checked:bg-primary peer-checked:border-primary transition-all duration-200 bg-black/40 group-hover:border-primary"></div>
                      <Check className="w-3.5 h-3.5 text-white absolute opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" />
                    </div>
                    <span className="text-sm text-zinc-300 uppercase font-bold tracking-tight group-hover:text-white transition-colors">
                      {item}
                    </span>
                  </label>)}
            </div>
            <FormDescription className="text-muted-foreground/60 text-[10px] uppercase tracking-wider mt-2">
              Select your primary areas of interest.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-primary/20 border border-primary/40 rounded-none text-primary text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <span className="text-xl">!</span> {submitError}
        </div>}

      <div className="space-y-4">
        {!isSubmitting ? <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-900 border border-white/10 rounded-none h-14 text-xl font-black uppercase tracking-widest shadow-lg transition-all hover:scale-[1.01]">
            Enlist
          </Button> : <div className="space-y-2">
            <div className="flex justify-between text-[10px] text-primary font-black uppercase tracking-widest">
              <span>Establishing Connection...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-white/10" />
          </div>}
      </div>
    </Form>;
}